import { CheckIcon } from "@heroicons/react/20/solid"
import useTranslations from "hooks/useTranslations"
import BookDemoButton from "./BookDemoButton"
import { BOOK_SECTION_ID } from "./constants"

/**
 * The offer block and the page's closing call to action.
 *
 * The application form that used to sit here is gone, on Kaisa's note: "we
 * actually don't need a form, let's just use the cal.com form". Everything it
 * collected — name, school, role — cal.com collects on the booking itself, and
 * a form that only promised a reply "within two working days" was a slower
 * path to the same meeting. PilotForm.tsx is still in this folder, unrendered,
 * because the Mailchimp tagging in it is the only record of how that audience
 * was segmented.
 */
export default function UkCtaSection() {
  const {
    ukSchoolsCtaTitle,
    ukSchoolsCtaLead,
    ukSchoolsCtaActionTitle,
    ukSchoolsCtaItems,
    ukSchoolsTrialTitle,
    ukSchoolsTrialLead,
    ukSchoolsCtaButton,
  } = useTranslations()

  return (
    <div className="relative bg-zinc-800 py-16 lg:py-24" id={BOOK_SECTION_ID}>
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* The heading spans both columns. Confined to the left one it broke
            across three lines while the right column sat empty above the card. */}
        <h2 className="m-0 mb-12 whitespace-pre-line font-display text-4xl font-extrabold leading-tight tracking-tight text-zinc-100 lg:text-5xl">
          {ukSchoolsCtaTitle}
        </h2>

        <div className="grid grid-cols-1 gap-y-12 lg:grid-cols-2 lg:gap-x-16">
          <div className="space-y-8">
            <p className="m-0 font-display text-lg leading-tight text-zinc-300 lg:text-xl">
              {ukSchoolsCtaLead}
            </p>

            <div className="space-y-4 border-l-8 border-kkm-lavender pl-5">
              <h3 className="m-0 font-display text-2xl text-zinc-100">
                {ukSchoolsCtaActionTitle}
              </h3>
              <ul className="m-0 list-none space-y-3 p-0">
                {(ukSchoolsCtaItems || []).map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckIcon
                      aria-hidden="true"
                      className="mt-0.5 h-6 w-6 shrink-0 text-kkm-lavender"
                    />
                    <span className="text-base text-zinc-100 lg:text-lg">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* The offer, then the one thing to do about it. On a lighter card so
              the CTA is the brightest thing in the band rather than another
              paragraph of the same grey. */}
          <div className="flex flex-col justify-center gap-6 rounded-2xl bg-zinc-700/60 p-8 ring-1 ring-white/10 lg:p-10">
            <h3 className="m-0 font-display text-2xl font-bold text-zinc-100 lg:text-3xl">
              {ukSchoolsTrialTitle}
            </h3>
            {/* Empty by default: this said the same thing as the second item
                in the checklist to its left, a few centimetres away. Kept as a
                key so the card can carry a line again without a code change. */}
            {ukSchoolsTrialLead ? (
              <p className="m-0 text-base leading-relaxed text-zinc-300 lg:text-lg">
                {ukSchoolsTrialLead}
              </p>
            ) : null}
            {/* Deliberately a different label from the hero's. The hero button
                sells the offer to someone who has read two lines; this one is
                clicked by someone who has read the page, so it can afford to
                say what actually happens next — a call — and stop cal.com's
                "15 min meeting" screen from contradicting the button that
                opened it. */}
            <div>
              <BookDemoButton label={ukSchoolsCtaButton} bgStyle="green" />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
